import React from 'react';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';

export interface PromoOverrideDetails {
    baseValue: number;
    percentUplift: number;
    effectiveValue: number;
    isRateOverridden: boolean;
}

export interface OverrideIndicatorCellProps {
    value: number | string;
    hasOverride: boolean;
    isPastOrLocked: boolean;
    isDuringPromotion?: boolean;
    hasUnsavedChanges?: boolean;
    onDotClick?: (e: React.MouseEvent) => void;
    promoOverrideDetails?: PromoOverrideDetails;
}

/**
 * Shared cell renderer that displays a value with optional status indicators:
 * - Orange dot: unsaved changes
 * - Blue dot: saved override (clickable to reset)
 */
const OverrideIndicatorCell: React.FC<OverrideIndicatorCellProps> = ({
    value,
    hasOverride,
    isPastOrLocked,
    isDuringPromotion = false,
    hasUnsavedChanges = false,
    onDotClick,
    promoOverrideDetails
}) => {
    const theme = useTheme();
    const isPromoOverride = Boolean(promoOverrideDetails);
    const percentUplift = promoOverrideDetails?.percentUplift ?? 0;
    const baseValue = promoOverrideDetails?.baseValue ?? 0;
    const effectiveValue = promoOverrideDetails?.effectiveValue ?? 0;
    const isRateOverridden = promoOverrideDetails?.isRateOverridden ?? false;
    const overrideDotColor = promoOverrideDetails
        ? (isRateOverridden ? theme.palette.primary.main : theme.palette.success.main)
        : theme.palette.primary.main;
    const displayValue = typeof value === 'number' ? value.toLocaleString() : value;
    
    const isResettable = hasOverride && !isPastOrLocked && !isDuringPromotion && Boolean(onDotClick);
    
    const handleDotClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isResettable) return;
        if (onDotClick) onDotClick(e);
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: isPromoOverride ? 'flex-end' : 'center',
                justifyContent: isPromoOverride ? 'center' : 'flex-end',
                flexDirection: isPromoOverride ? 'column' : 'row',
                gap: isPromoOverride ? 0.25 : 0,
                position: 'relative',
                pr: 1,
                py: isPromoOverride ? 0.5 : 0
            }}
        >
            {(hasUnsavedChanges || hasOverride) && (
                <Box sx={{ position: 'absolute', top: 2, right: 2, display: 'flex', gap: 0.25 }}>
                    {hasUnsavedChanges && (
                        <Box
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: theme.palette.warning.main,
                                boxShadow: `0 0 0 1px ${theme.palette.background.paper}`,
                            }}
                        />
                    )}
                    {hasOverride && !hasUnsavedChanges && (
                        <Tooltip title={isPromoOverride ? 'Promotion applies' : ''}>
                            <Box 
                                sx={{ 
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: overrideDotColor,
                                    boxShadow: `0 0 0 1px ${theme.palette.background.paper}`,
                                    cursor: isResettable ? 'pointer' : 'default',
                                    zIndex: 1
                                }}
                                onClick={isResettable ? handleDotClick : undefined}
                            />
                        </Tooltip>
                    )}
                </Box>
            )}
            {isPromoOverride ? (
                <>
                    <Typography
                        variant="caption"
                        sx={{
                            fontSize: percentUplift > 0 ? '12px' : '14px',
                            lineHeight: 1.2,
                            textAlign: 'right'
                        }}
                    >
                        {baseValue.toLocaleString()}
                    </Typography>
                    {percentUplift > 0 && (
                        <Typography
                            variant="caption"
                            sx={{
                                fontSize: '12px',
                                color: theme.palette.success.main,
                                lineHeight: 1.2,
                                textAlign: 'right'
                            }}
                        >
                            +{percentUplift}%
                        </Typography>
                    )}
                    {percentUplift > 0 && (
                        <Typography
                            variant="caption"
                            sx={{
                                fontSize: '14px',
                                color: theme.palette.text.primary,
                                lineHeight: 1.2,
                                textAlign: 'right'
                            }}
                        >
                            {effectiveValue.toLocaleString()}
                        </Typography>
                    )}
                </>
            ) : (
                <Typography variant="body2" sx={{ textAlign: 'right', width: '100%' }}>
                    {displayValue}
                </Typography>
            )}
        </Box>
    );
};

export default OverrideIndicatorCell;
